/**
 * Normalises any common YouTube URL into the canonical
 * https://www.youtube.com/watch?v=ID form.
 *
 * Supported input shapes (with optional ?feature=share / ?t=120 / ...):
 *   - https://youtube.com/watch?v=ID
 *   - https://youtu.be/ID
 *   - https://youtube.com/live/ID
 *   - https://youtube.com/shorts/ID
 *   - https://youtube.com/embed/ID
 *   - Any subdomain (www., m., music.)
 *
 * Returns the input unchanged if not recognised as YouTube.
 * Idempotent — feeding the canonical form back returns the same string.
 */
const PATTERNS = [
  /(?:www\.|m\.|music\.)?youtube\.com\/watch\?(?:[^#]*&)?v=([A-Za-z0-9_-]{11})/,
  /youtu\.be\/([A-Za-z0-9_-]{11})/,
  /(?:www\.|m\.|music\.)?youtube\.com\/(?:live|shorts|embed)\/([A-Za-z0-9_-]{11})/,
];

export function normalizeYouTubeUrl(url) {
  if (!url || typeof url !== 'string') return url;
  const text = url.trim();
  if (!text) return text;
  if (!text.includes('youtube.com') && !text.includes('youtu.be')) return text;
  for (const pat of PATTERNS) {
    const m = text.match(pat);
    if (m) return `https://www.youtube.com/watch?v=${m[1]}`;
  }
  return text; // YouTube domain but unparseable — leave unchanged
}

/** Convenience: did normalisation actually rewrite the URL? */
export function youTubeUrlWasRewritten(input, normalized) {
  if (!input || !normalized) return false;
  return input.trim() !== normalized;
}

// ============== Facebook + Twitch URL normalization ==============
// Mirrors backend `normalize_facebook_url` / `normalize_twitch_url`.
// Conservative cleanup: rewrite mobile/web subdomains → www, drop tracking
// query params (fbclid, utm_*, mibextid, etc.). Idempotent.

const TRACKING_PARAMS = new Set([
  'fbclid', 'igshid', '_rdc', '_rdr', 'mibextid', 'ref',
  'ref_src', 'ref_url', 'tn', 'rdr', 'share_id',
]);

function stripTrackingParams(url) {
  try {
    const u = new URL(url);
    const before = u.search;
    const keep = [];
    u.searchParams.forEach((v, k) => {
      if (!(k.startsWith('utm_') || TRACKING_PARAMS.has(k))) keep.push([k, v]);
    });
    u.search = '';
    keep.forEach(([k, v]) => u.searchParams.append(k, v));
    if (u.search === before) return url; // nothing to strip → preserve original quoting
    return u.toString();
  } catch {
    return url;
  }
}

export function normalizeFacebookUrl(url) {
  if (!url || typeof url !== 'string') return url;
  let text = url.trim();
  if (!text) return text;
  if (!text.includes('facebook.com') && !text.includes('fb.watch')) return text;
  text = text.replace(/^https?:\/\/m\.facebook\.com/i, 'https://www.facebook.com');
  text = text.replace(/^https?:\/\/web\.facebook\.com/i, 'https://www.facebook.com');
  text = text.replace(/facebook\.com\/watch\/live\/\?v=/i, 'facebook.com/watch/?v=');
  text = stripTrackingParams(text);
  return text;
}

export function normalizeTwitchUrl(url) {
  if (!url || typeof url !== 'string') return url;
  let text = url.trim();
  if (!text) return text;
  if (!text.includes('twitch.tv')) return text;
  text = text.replace(/^https?:\/\/m\.twitch\.tv/i, 'https://www.twitch.tv');
  text = text.replace(/^https?:\/\/go\.twitch\.tv/i, 'https://www.twitch.tv');
  text = stripTrackingParams(text);
  return text;
}

/** Provider-aware dispatcher. Falls through unchanged for non-supported providers. */
export function normalizeStreamUrl(url, provider) {
  if (!url || !provider) return url;
  const p = String(provider).toLowerCase();
  if (p === 'youtube') return normalizeYouTubeUrl(url);
  if (p === 'facebook') return normalizeFacebookUrl(url);
  if (p === 'twitch') return normalizeTwitchUrl(url);
  return url;
}

export function streamUrlWasRewritten(input, normalized) {
  if (!input || !normalized) return false;
  return input.trim() !== normalized;
}
